import React from 'react';
import { useTranslation } from 'react-i18next';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';

import { useAppSelector } from '@/store';
import { resolveMainWalletForCurrency } from '@/store/slices/wallet';
import { stripeLocale } from '@/utils/stripe-locale';

import { Button, Icon } from '../../../../components';
import { COLORS } from '../../../../theme/tokens';
import { getStripe, useStripeAppearance, fetchInvoiceClientSecret } from '../../../configurator-v3/data/payments';
import { currencySymbol, formatAmountWithCode } from '../../../../utils/money';
import { DEFAULT_CURRENCY } from '../../../../utils/catalog';
import { Sheet } from '@/nova/components/Sheet';
import type { PaymentMethodId } from '../PaymentModal';
import {
  HeaderId,
  IconTile,
  IconArt,
  HeaderText,
  NameRow,
  Name,
  TierPill,
  PriceRow,
  Price,
  Subtitle,
  OptionCard,
  OptionRow,
  Radio,
  OptionText,
  OptionLabel,
  OptionHint,
  OptionNote,
  OptionDrawer,
  OptionDrawerInner,
  StripeArea,
  StripeSectionLabel,
  TotalRow,
  TotalLabel,
  TotalCol,
  TotalPrice,
} from '../PaymentModal/PaymentModal.styles';
import { CardStatusNote, Footnote } from './FinishPaymentModal.styles';

export type FinishPaymentModalProps = {
  open: boolean;
  /** Feature artwork URL. */
  image: string;
  /** Feature display name. */
  name: string;
  /** The tier the pending invoice pays for ("Pro"). */
  tierName: string;
  /** The invoice's committed amount (major units) — settled in full, no proration. */
  amount: number;
  /** 3-letter currency code of the invoice ("USD"). */
  currency: string;
  /** The invoice's session id — used to fetch a Stripe intent for the CARD path. `null` when the
   *  invoice has no session, in which case the card option shows as unavailable and the wallet
   *  path is the way to settle. */
  sessionId: string | null;
  /** Wallet settlement in flight (parent-owned: pay-invoice + refetch) — disables the CTA. */
  busy?: boolean;
  onClose: () => void;
  /** Settle CTA. `wallet` fires immediately (parent pays the invoice from the MAIN balance);
   *  `card` fires only AFTER a successful in-modal Stripe confirmation (parent then refetches). */
  onPay: (method: PaymentMethodId) => void;
};

/** Card path readiness: the invoice's Stripe intent is fetched lazily when the card option is
 *  first opened. `unavailable` = no session / the intent couldn't be created (e.g. the session
 *  carries no email yet) — the wallet path still settles the invoice. */
type CardState = 'idle' | 'loading' | 'ready' | 'unavailable';

/** The Stripe Payment Element + confirm logic, mounted inside <Elements> bound to the invoice's
 *  PaymentIntent clientSecret (intent-first, the same wiring the wallet-deposit + configurator
 *  checkouts use). Registers its confirm into `confirmRef` so the modal's Pay button (outside
 *  <Elements>) can trigger it, and reports confirming/error/paid back up. On success the invoice
 *  is settled server-side (webhook) and the parent refetches. */
function CardConfirm(p: {
  clientSecret: string;
  confirmRef: React.MutableRefObject<(() => void) | null>;
  setConfirming: (value: boolean) => void;
  setError: (value: string | null) => void;
  onPaid: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const { clientSecret, confirmRef, setConfirming, setError, onPaid } = p;
  // Synchronous re-entry guard: the Pay button's disabled state only reflects `confirming` on
  // the NEXT render, so a fast second click could fire a second confirmPayment on the same
  // intent before then. This ref bails immediately.
  const inFlight = React.useRef(false);

  React.useEffect(() => {
    confirmRef.current = async (): Promise<void> => {
      if (!stripe || !elements || inFlight.current) return;
      inFlight.current = true;
      setConfirming(true);
      setError(null);
      const fail = (message: string | undefined): void => {
        setError(message ?? t('Payment failed. Please try again.'));
        setConfirming(false);
        inFlight.current = false;
      };
      const submitResult = await elements.submit();
      if (submitResult.error) {
        fail(submitResult.error.message);
        return;
      }
      const result = await stripe.confirmPayment({
        elements,
        clientSecret,
        redirect: 'if_required',
        confirmParams: { return_url: window.location.origin + window.location.pathname },
      });
      if (result.error) {
        fail(result.error.message);
        return;
      }
      // Charged. Clear the in-modal busy/confirming BEFORE handing off so a slow (or failing)
      // parent refetch can't leave the CTA wedged on "Processing…". The parent closes the modal.
      setConfirming(false);
      inFlight.current = false;
      onPaid();
    };
    return () => {
      confirmRef.current = null;
    };
  }, [stripe, elements, clientSecret, confirmRef, setConfirming, setError, onPaid, t]);

  return <PaymentElement options={{ layout: { type: 'accordion', defaultCollapsed: false } }} />;
}

/**
 * "Finish payment" modal for a feature the owner began activating / upgrading but never paid
 * for: a pending invoice already fixes the tier and the amount, so there is no tier choice left,
 * only the payment. Same two options as the "Pay to activate" modal (reusing its visual parts):
 *  - **Your wallet** — settles the invoice from the MAIN balance (`pay-invoice`), the reliable
 *    in-app path. Disabled + noted when the balance can't cover it.
 *  - **Card / other** — confirms a real Stripe intent fetched for the invoice's session. When no
 *    intent can be created (no session, or the session has no email yet) the option shows as
 *    "not available yet" and the wallet path remains.
 *
 * The amount is the invoice's committed price (no proration — the invoice is already issued).
 */
export const FinishPaymentModal = ({
  open,
  image,
  name,
  tierName,
  amount,
  currency,
  sessionId,
  busy = false,
  onClose,
  onPay,
}: FinishPaymentModalProps): React.ReactElement | null => {
  const { t, i18n } = useTranslation();
  const appearance = useStripeAppearance();

  const [method, setMethod] = React.useState<PaymentMethodId>('wallet');
  const [cardState, setCardState] = React.useState<CardState>('idle');
  const [cardSecret, setCardSecret] = React.useState<string | null>(null);
  const [cardError, setCardError] = React.useState<string | null>(null);
  const [confirming, setConfirming] = React.useState(false);
  // Set by CardConfirm (inside <Elements>) so the Pay button can trigger the confirm.
  const confirmRef = React.useRef<(() => void) | null>(null);

  const currencyCode = currency || DEFAULT_CURRENCY;
  const money = (value: number): string => `${currencySymbol(currencyCode)}${formatAmountWithCode(value, currencyCode)}`;
  const amountText = money(amount);
  const amountCents = Math.round(amount * 100);

  // Pay from the MAIN wallet in the invoice's currency (the selected casino's billing currency)
  // so the balance reads in the same currency as the amount due — falling back to the primary
  // MAIN wallet when the owner has none in that currency. Null until the always-mounted TopBar
  // has loaded the wallet list.
  const ownerWallet = useAppSelector((state) => state.ownerWallet ?? null);
  const walletView = resolveMainWalletForCurrency(ownerWallet, currencyCode);
  const walletAvailable = walletView.balance;
  const walletBalance =
    walletAvailable !== null
      ? `${currencySymbol(walletView.currency)}${formatAmountWithCode(walletAvailable, walletView.currency)}`
      : null;
  // Can the balance cover the full invoice? While it's still loading (null) we don't block.
  const walletShort = walletAvailable !== null && Math.round(walletAvailable * 100) < amountCents;
  // A short wallet forces the card path (whatever the last choice was) — purely derived.
  const selectedMethod: PaymentMethodId = walletShort ? 'card' : method;

  // Reset to a clean state whenever the modal closes so every open starts on the wallet option
  // with no stale card intent / error.
  React.useEffect(() => {
    if (!open) {
      setMethod('wallet');
      setCardState('idle');
      setCardSecret(null);
      setCardError(null);
      setConfirming(false);
    }
  }, [open]);

  // Lazily create the invoice's Stripe intent the first time the card option is opened. A missing
  // session id (or a backend refusal — e.g. the session has no email) leaves the card path
  // `unavailable`; the wallet path still settles the invoice. `t` is intentionally not a dep (it
  // must not re-mint the intent on a language switch).
  React.useEffect(() => {
    if (!open || selectedMethod !== 'card' || cardState !== 'idle') return;
    if (!sessionId) {
      setCardState('unavailable');
      return;
    }
    let cancelled = false;
    setCardState('loading');
    void (async () => {
      try {
        const secret = await fetchInvoiceClientSecret(sessionId);
        if (!cancelled) {
          setCardSecret(secret);
          setCardState('ready');
        }
      } catch {
        if (!cancelled) setCardState('unavailable');
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedMethod, cardState, sessionId]);

  const onSelectWallet = React.useCallback(() => setMethod('wallet'), []);
  const onSelectCard = React.useCallback(() => setMethod('card'), []);
  const onCardPaid = React.useCallback(() => onPay('card'), [onPay]);

  const onPayClick = React.useCallback(() => {
    // Don't re-enter while a settle is already in flight (wallet or an in-modal card confirm).
    if (busy || confirming) return;
    if (selectedMethod === 'wallet') {
      onPay('wallet');
      return;
    }
    // Card: trigger the in-modal Stripe confirm; onPay('card') fires from CardConfirm on success.
    void confirmRef.current?.();
  }, [busy, confirming, selectedMethod, onPay]);

  if (!open) {
    return null;
  }

  // The CTA is unusable while a settle is in flight, and — on the card path — until the intent is
  // ready to confirm.
  const cardBlocked = selectedMethod === 'card' && cardState !== 'ready';
  const payDisabled = busy || confirming || cardBlocked;
  const payLabel = busy || confirming ? t('Processing…') : t('Pay {{amount}}', { amount: amountText });

  return (
    <Sheet
      ariaLabel="{{name}}"
      ariaLabelPlaceholders={{ name }}
      maxHeight="100%"
      onClose={onClose}
      header={
        <HeaderId>
          <IconTile>
            <IconArt src={image} alt="" />
          </IconTile>
          <HeaderText>
            <NameRow>
              <Name text={name} tag="span" />
              <TierPill text={tierName} tag="span" />
            </NameRow>
            <PriceRow>
              <Price
                text="{{symbol}}{{price}} /month"
                placeholders={{ symbol: currencySymbol(currencyCode), price: formatAmountWithCode(amount, currencyCode) }}
                tag="span"
              />
            </PriceRow>
          </HeaderText>
        </HeaderId>
      }
    >
      <Subtitle text="Finish paying to activate your {{name}} subscription" placeholders={{ name }} tag="span" />

      <OptionCard>
        <OptionRow type="button" onClick={onSelectWallet} disabled={walletShort} $disabled={walletShort}>
          <Radio $selected={selectedMethod === 'wallet'}>
            {selectedMethod === 'wallet' ? <Icon name="check" size={10} color={COLORS.onAccent} /> : null}
          </Radio>
          <OptionText>
            {walletBalance ? (
              <OptionLabel text="Your wallet · ({{balance}})" placeholders={{ balance: walletBalance }} tag="span" />
            ) : (
              <OptionLabel text="Your wallet" tag="span" />
            )}
            <OptionHint
              text="Pay {{price}} from your balance to complete this payment"
              placeholders={{ price: amountText }}
              tag="span"
            />
          </OptionText>
        </OptionRow>
        {walletShort ? <OptionNote text="Not enough balance to pay from your wallet." tag="span" /> : null}
      </OptionCard>

      <OptionCard>
        <OptionRow type="button" onClick={onSelectCard}>
          <Radio $selected={selectedMethod === 'card'}>
            {selectedMethod === 'card' ? <Icon name="check" size={10} color={COLORS.onAccent} /> : null}
          </Radio>
          <OptionText>
            <OptionLabel text="Credit card or other options" tag="span" />
            <OptionHint text="Pick your preferred method" tag="span" />
          </OptionText>
        </OptionRow>
        <OptionDrawer $open={selectedMethod === 'card'} aria-hidden={selectedMethod !== 'card'}>
          <OptionDrawerInner>
            <StripeArea>
              {cardState === 'loading' ? (
                <CardStatusNote text="Preparing secure checkout…" tag="span" />
              ) : null}
              {cardState === 'unavailable' ? (
                <CardStatusNote
                  text="Card payment isn't available for this invoice yet. Pay from your wallet to complete it."
                  tag="span"
                />
              ) : null}
              {cardState === 'ready' && cardSecret ? (
                <Elements
                  // `locale` is NOT mutable after mount, unlike `appearance` — keying on it
                  // remounts Elements on a language switch instead of leaving Stripe stuck in
                  // the language the modal opened in.
                  key={stripeLocale(i18n.language)}
                  stripe={getStripe()}
                  options={{
                    clientSecret: cardSecret,
                    appearance,
                    // Stripe renders its own copy (field labels, validation errors) and defaults
                    // to the BROWSER's language rather than ours unless told explicitly.
                    locale: stripeLocale(i18n.language),
                  }}
                >
                  <StripeSectionLabel text="Payment method" tag="span" />
                  <CardConfirm
                    clientSecret={cardSecret}
                    confirmRef={confirmRef}
                    setConfirming={setConfirming}
                    setError={setCardError}
                    onPaid={onCardPaid}
                  />
                </Elements>
              ) : null}
              {cardError ? <CardStatusNote text={cardError} tag="span" /> : null}
            </StripeArea>
          </OptionDrawerInner>
        </OptionDrawer>
      </OptionCard>

      <TotalRow>
        <TotalLabel text="Total due today" tag="span" />
        <TotalCol>
          <TotalPrice>{amountText}</TotalPrice>
        </TotalCol>
      </TotalRow>

      <Button variant="primary" fullWidth disabled={payDisabled} label={payLabel} onClick={onPayClick} />

      <Footnote
        text="This settles your pending invoice and activates {{name}} {{tier}}."
        placeholders={{ name, tier: tierName }}
        tag="span"
      />
    </Sheet>
  );
};

export default FinishPaymentModal;
